import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, LogIn, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { validateEmail, sanitizeInput, checkRateLimit } from '../utils/validation';
import '../styles/auth.css';


const LoginPage = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/dashboard';

  const handleChange = (e) => {
    setError('');
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: sanitizeInput(e.target.value),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Client-side rate limiting
    if (!checkRateLimit('login_attempts', 5, 15 * 60 * 1000)) {
      setError('Too many login attempts. Please wait 15 minutes and try again.');
      return;
    }

    if (!validateEmail(formData.email)) {
      setError('Please enter a valid email address.');
      return;
    }

    if (!formData.password) {
      setError('Please enter your password.');
      return;
    }

    setLoading(true);
    const { error } = await signIn({
      email: formData.email,
      password: formData.password,
    });
    setLoading(false);

    if (error) {
      // Generic message — don't reveal whether email exists
      setError('Invalid email or password. Please try again.');
    } else {
      navigate(from, { replace: true });
    }
  };

  return (
    <div className="auth-wrapper">
      <div className="auth-bg-pattern" />

      <div className="auth-card">
        <div className="auth-logo">
          <div className="auth-logo-icon">
            <svg width="32" height="28" viewBox="0 0 32 28" fill="none">
              <rect x="0" y="8" width="6" height="20" rx="1" fill="#E8471E"/>
              <rect x="9" y="4" width="6" height="24" rx="1" fill="#E8471E"/>
              <rect x="18" y="0" width="6" height="28" rx="1" fill="#E8471E"/>
              <rect x="26" y="6" width="6" height="22" rx="1" fill="#C13010"/>
            </svg>
          </div>
          <span className="auth-logo-text">SYNFIN</span>
        </div>

        <h1 className="auth-title">Welcome back</h1>
        <p className="auth-subtitle">Sign in to your account to continue</p>

        {error && (
          <div className="auth-error">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label className="form-label" htmlFor="email">Email address</label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              className="form-input"
              placeholder="you@example.com"
              value={formData.email}
              onChange={handleChange}
              required
              maxLength={254}
            />
          </div>

          <div className="form-group">
            <div className="form-label-row">
              <label className="form-label" htmlFor="password">Password</label>
              <Link to="/forgot-password" className="form-label-link">
                Forgot password?
              </Link>
            </div>
            <div className="input-with-icon">
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                className="form-input"
                placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
                required
                maxLength={128}
              />
              <button
                type="button"
                className="input-icon-btn"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className={`btn-primary${loading ? ' btn-loading' : ''}`}
            disabled={loading}
          >
            {loading ? (
              <span className="btn-spinner" />
            ) : (
              <>
                <LogIn size={18} />
                Sign in
              </>
            )}
          </button>
        </form>

        <p className="auth-footer-text">
          Don't have an account?{' '}
          <Link to="/register" className="auth-link">Create one free</Link>
        </p>
      </div>

      <p className="auth-legal">
        By continuing, you agree to SynFin's{' '}
        <a href="/terms" className="auth-link">Terms of Service</a> and{' '}
        <a href="/privacy" className="auth-link">Privacy Policy</a>.
      </p>
    </div>
  );
};

export default LoginPage;
